/**
 * Punctuation-level clause splitting (plan §5).
 *
 * @remarks
 * Consumes the clause separators the folding stage preserved (`. , ; ! ?`):
 * `take the card, swipe the panel. descend!` → three clause strings. Runs of
 * separators collapse to one boundary; empty clauses (leading/trailing/doubled
 * separators, or punctuation-only input) are dropped, so `go north.` yields
 * exactly one clause and `!!!` yields none.
 *
 * D40 — a SINGLE separator welded between letters is a typo, not a boundary.
 * Real clause punctuation is followed by space or end of input; `kettl;e` is a
 * slipped finger, and splitting it invented a second clause whose stray `e`
 * matched the east shorthand and silently walked the player into another room
 * (found by Mike). Such separators are left in place, where the fuzzy pass
 * repairs them like any other one-character typo. A RUN of two or more is
 * deliberate whatever surrounds it — `look…descend` folds to `look...descend`
 * and chains exactly as the player meant.
 *
 * Conjunction-level splitting (`then` / clause-leading `and`) is a separate,
 * vocabulary-aware pass — see `split-on-conjunctions`.
 *
 * @packageDocumentation
 */

// cspell:ignore kettl -- the D40 repro: a typo split by word-internal punctuation

/**
 * The separator runs that actually END a clause (D40): a run of two or more
 * anywhere, or a single separator followed by whitespace or end of input. A
 * lone separator with word characters on both sides is a typo and stays put.
 */
const CLAUSE_SEPARATOR_RUN_PATTERN = /[!,.;?]{2,}|[!,.;?](?=\s|$)/u;

/**
 * Split folded input into punctuation-delimited clause strings.
 *
 * @param foldedText - Output of `foldCharacters`
 * @returns Trimmed, non-empty clause strings in input order
 */
export const splitOnSeparators = (foldedText: string): readonly string[] =>
	foldedText
		.split(CLAUSE_SEPARATOR_RUN_PATTERN)
		.map((clauseCandidate) => clauseCandidate.trim())
		.filter((clauseCandidate) => clauseCandidate !== '');
